import { Calendra2 } from "@common/DatePicker";
import ReactSelect from "@common/ReactSelect";
import { DEFAULT_INPUTFIELD_LENGTH, FIELD_TYPE, MOBILE_PREFIX } from "@src/config/constant";
import { useState } from "react";

const isDestinationCountryField = (fieldItem: any) => ["destination_country", "dest_country_id"].includes(fieldItem?.name);
const isFieldVisibleOnWeb = (fieldItem: any) => fieldItem?.is_show === true && fieldItem?.for_web === true;
const isTravellerMobileField = (fieldItem: any) => (
    fieldItem?.type === FIELD_TYPE.TEXT_BOX
    && (/^(mobile|mobile_number)_\d+$/.test(fieldItem?.name) || ["emer_number_0"].includes(fieldItem?.name))
);

export const isShowTravelDynamicField = (fieldItem: any, blocks: any) => {
    if (!isFieldVisibleOnWeb(fieldItem)) return false;

    if (fieldItem?.field_show) {
        const splitType = fieldItem.field_show.split("/");
        if (splitType.length > 1) {
            const expectedValues = splitType[1].includes("@") ? splitType[1].split("@") : [splitType[1]];
            const fields = blocks?.flatMap((blockItem: any) => blockItem?.fields || []) || [];
            const dependencyField = fields.find((item: any) => (item.base_name || item.name) === splitType[0]);
            const actualValue = String(dependencyField?.value?.value ?? dependencyField?.value);

            return expectedValues.includes(actualValue);
        }
    }

    return true;
};

export default function DynamicFormField(props: any) {
    const [, setRefresh] = useState(1);
    const rowClass = props.rowClass || "col-md-4";
    const shouldRenderField = props.shouldRenderField || (() => true);

    const getFieldValue = (fieldItem: any) => fieldItem.value?.label ?? fieldItem.value ?? "";

    const getOptionValue = (fieldItem: any, optionsList: any[]) => {
        if (typeof fieldItem.value === "object") return fieldItem.value;
        if (fieldItem.value || fieldItem.value === 0) {
            return optionsList?.find((option: any) => option.value == fieldItem.value) || null; // eslint-disable-line
        }
        return null;
    };

    const clearChildDependencies = (fieldItem: any) => {
        const childDependencies = fieldItem?.child_depedencies || [];

        childDependencies.forEach((key: string) => {
            const fieldData = props?.fields?.find((item: any) => item.name === key || item.base_name === key);
            if (fieldData) {
                fieldData.value = "";
                fieldData.error_message = "";
            }
        });
    };

    const updateField = (fieldItem: any, value: any) => {
        fieldItem.value = value;
        fieldItem.error_message = "";
        clearChildDependencies(fieldItem);
        setRefresh((prev) => prev + 1);
        props.onValueChange?.(fieldItem);
    };

    return (
        <>
            {props?.fields?.map((fieldItem: any, index: number) => {
                const isShowField = isShowTravelDynamicField(fieldItem, props.blocks);
                const optionsList = fieldItem?.options || [];
                const optionValue = getOptionValue(fieldItem, optionsList);
                const selectMenuOpen = props.getSelectMenuOpen?.(fieldItem);
                const handleSelectMenuOpen = () => props.onSelectFieldClick?.(fieldItem);

                if (!isShowField || !shouldRenderField(fieldItem)) return null;

                switch (fieldItem.type) {
                    case FIELD_TYPE.TEXT_BOX:
                    case FIELD_TYPE.MOBILE_FIELD:
                    case FIELD_TYPE.CURRENCY:
                    case FIELD_TYPE.TELEPHONE:
                    case FIELD_TYPE.SUM:
                    case FIELD_TYPE.EMAIL: {
                        const inputType = fieldItem.type === FIELD_TYPE.EMAIL || fieldItem.name === "email" ? "email" : "text";
                        const showMobilePrefix = isTravellerMobileField(fieldItem);
                        
                        return (
                            <fieldset className={rowClass} key={fieldItem.id || fieldItem.name || index}>
                                <div className={`material`}>
                                    <input
                                        maxLength={showMobilePrefix ? 14 : fieldItem.type === FIELD_TYPE.MOBILE_FIELD ? 10 : DEFAULT_INPUTFIELD_LENGTH}
                                        disabled={!fieldItem.is_enable}
                                        type={inputType}
                                        placeholder=" "
                                        name={fieldItem.name}
                                        className={`form-input${showMobilePrefix ? "" : ""}`}
                                        value={
                                            showMobilePrefix
                                                ? `${MOBILE_PREFIX} ${getFieldValue(fieldItem) ?? ""}`
                                                : getFieldValue(fieldItem)
                                        }
                                        onChange={(event) => {
                                            if (!showMobilePrefix) {
                                                updateField(fieldItem, event.target.value);
                                                return;
                                            }

                                            const inputValue = String(event.target.value || "");
                                            const withoutPrefix = inputValue.trim().startsWith("+63")
                                                ? inputValue.trim().slice(3)
                                                : inputValue;
                                            const digitsOnly = withoutPrefix.replace(/\D/g, "").slice(0, 10);
                                            updateField(fieldItem, digitsOnly);
                                        }}
                                    />
                                    <label data-label={fieldItem.label + (fieldItem.is_required ? "*" : "")} className="form-label"></label>
                                    {fieldItem?.error_message ? <span className="error-txt">{fieldItem.error_message}</span> : null}
                                </div>
                            </fieldset>
                        );
                    }

                    case FIELD_TYPE.READ_ONLY_TEXT_BOX: {
                        const isDestinationCountry = isDestinationCountryField(fieldItem);
                        const isClickableReadonly = isDestinationCountry && props.onReadOnlyFieldClick;
                        return (
                            <fieldset className={`${rowClass} form-filed`} key={fieldItem.id || fieldItem.name || index}>
                                <div
                                    className={`material animation-effect active-label travel-readonly-field ${isDestinationCountry ? "travel-destination-field" : ""}${isClickableReadonly ? " travel-readonly-clickable" : ""}`}
                                    onClick={() => isClickableReadonly && props.onReadOnlyFieldClick(fieldItem)}
                                    onKeyDown={(event) => {
                                        if (!isClickableReadonly || !["Enter", " "].includes(event.key)) return;
                                        event.preventDefault();
                                        props.onReadOnlyFieldClick(fieldItem);
                                    }}
                                    role={isClickableReadonly ? "button" : undefined}
                                    tabIndex={isClickableReadonly ? 0 : undefined}
                                >
                                    <input
                                        disabled={!isClickableReadonly}
                                        readOnly={isClickableReadonly}
                                        type="text"
                                        placeholder=" "
                                        name={fieldItem.name}
                                        className="form-input"
                                        value={getFieldValue(fieldItem)}
                                    />
                                    <label data-label={fieldItem.label + (fieldItem.is_required ? "*" : "")} className="form-label"></label>
                                    {fieldItem?.error_message ? <span className="error-txt">{fieldItem.error_message}</span> : null}
                                </div>
                            </fieldset>
                        );
                    }

                    case FIELD_TYPE.DATE_PICKER:
                    case FIELD_TYPE.DATE_RANGE:
                    case FIELD_TYPE.FUTURE_DATE: {
                        return (
                            <fieldset className={`form-filed date-picker-detail ${rowClass}`} key={fieldItem.id || fieldItem.name || index}>
                                <div className={`material animation-effect${fieldItem.value ? " active-label" : ""}`}>
                                    <Calendra2
                                        onChange={(date: Date) => updateField(fieldItem, date)}
                                        placeholder={fieldItem.label + (fieldItem.is_required ? "*" : "")}
                                        selected={fieldItem.value}
                                        dateFormat="dd/MM/yyyy"
                                        disabled={!fieldItem.is_enable}
                                        minDate={fieldItem.minDate}
                                        maxDate={fieldItem.maxDate}
                                        fieldId={fieldItem.id}
                                        fields={props?.fields}
                                        fieldLabel={fieldItem.label}
                                        fieldname={fieldItem.name}
                                    />
                                    {fieldItem?.error_message ? <span className="error-txt">{fieldItem.error_message}</span> : null}
                                </div>
                            </fieldset>
                        );
                    }

                    case FIELD_TYPE.RADIO_BOX: {
                        const showComingSoonPill = fieldItem?.options?.some((option: any) => option?.coming_soon);
                        return (
                            <div className={`${rowClass} m-xl-b radio-btn existing-loan-fied-radio`} key={fieldItem.id || fieldItem.name || index}>
                                <div className="m-lg-r f-13 travel-radio-heading">
                                    <span>{fieldItem.label}{fieldItem.is_required ? "*" : ""}</span>
                                    {showComingSoonPill ? <span className="travel-coming-soon-pill">Coming Soon</span> : null}
                                </div>
                                <div className="loan-radio-outer d-flex p-sm-t">
                                    {fieldItem?.options?.map((option: any, optionIndex: number) => {
                                        const isOptionDisabled = !fieldItem.is_enable || !!option?.disabled;
                                        return (
                                            <div key={`${fieldItem.name}-${optionIndex}`} className={`input-radio travel-radio-option${isOptionDisabled ? " is-disabled" : ""}`}>
                                                <input
                                                    checked={String(fieldItem?.value?.value ?? fieldItem?.value) === String(option?.value)}
                                                    id={`${fieldItem.name}-${optionIndex}`}
                                                    type="radio"
                                                    name={fieldItem.name}
                                                    onChange={() => updateField(fieldItem, option)}
                                                    disabled={isOptionDisabled}
                                                />
                                                <label className="radio-label" htmlFor={`${fieldItem.name}-${optionIndex}`}>{option.label}</label>
                                            </div>
                                        );
                                    })}
                                </div>
                                {fieldItem?.error_message ? <span className="error-txt">{fieldItem.error_message}</span> : null}
                            </div>
                        );
                    }

                    default:
                        return (
                            <ReactSelect
                                key={fieldItem.id || fieldItem.name || index}
                                options={optionsList}
                                value={optionValue}
                                handleChange={(value: any) => updateField(fieldItem, value)}
                                placeholder={fieldItem.label + (fieldItem.is_required ? "*" : "")}
                                fieldKey=""
                                fieldItemName={fieldItem.name}
                                isDisabled={!fieldItem.is_enable}
                                errorField={fieldItem?.error_message}
                                fieldSetClassName={`form-filed ${rowClass}`}
                                menuIsOpen={selectMenuOpen}
                                onMenuOpen={handleSelectMenuOpen}
                            />
                        );
                }
            })}
        </>
    );
}
